import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Avatar, message, Space, Typography } from 'antd';
import { UserOutlined, PhoneOutlined, CameraOutlined, SaveOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { auth_api } from '../api/auth-api';
import { auth_actions } from '../redux/modules/auth';
import LoadingSpin from '../components/Loading/LoadingSpin';

const { Title, Text } = Typography;

export default function Profile() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();

  // Luôn load dữ liệu mới nhất từ database khi component mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoadingUser(true);
        console.log('Profile: Loading user data from database...');
        const userResponse = await auth_api.getMe();
        if (userResponse.success && userResponse.user) {
          console.log('Profile: Loaded user from database:', userResponse.user);
          // Cập nhật Redux store với dữ liệu mới nhất
          const userData = {
            ...userResponse.user,
            displayName: userResponse.user.displayName || '',
            photoURL: userResponse.user.photoURL || '',
            phoneNumber: userResponse.user.phoneNumber || '',
            email: userResponse.user.email || ''
          };
          dispatch(auth_actions.set_user(userData));
          // Cập nhật form với dữ liệu mới nhất
          form.setFieldsValue({
            displayName: userData.displayName || '',
            photoURL: userData.photoURL || '',
            phoneNumber: userData.phoneNumber || '',
            email: userData.email || ''
          });
        } else {
          console.error('Profile: Failed to load user data:', userResponse);
          message.error('Không thể tải thông tin user từ database');
        }
      } catch (error) {
        console.error('Profile: Error loading user data:', error);
        message.error('Lỗi khi tải thông tin user: ' + (error.message || 'Unknown error'));
      } finally {
        setLoadingUser(false);
      }
    };
    
    loadUserData();
  }, [form, dispatch]); // Chỉ chạy khi component mount, không phụ thuộc vào user


  const onFinish = async (values) => {
    try {
      setLoading(true);
      
      const updateData = {
        displayName: values.displayName || '',
        photoURL: values.photoURL || '',
        phoneNumber: values.phoneNumber || ''
      };
      
      const result = await auth_api.updateProfile(updateData);
      
      console.log('Update profile result:', result);
      
      // Kiểm tra result có tồn tại không
      if (!result) {
        throw new Error('Response không hợp lệ');
      }
      
      if (result.success) {
        // Cập nhật user trong Redux store
        if (result.user) {
          dispatch(auth_actions.set_user(result.user));
        }
        message.success('Cập nhật thông tin thành công!');
      } else {
        message.error(result.message || 'Cập nhật thông tin thất bại');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      // Xử lý error từ API
      const errorMessage = error.message || error.response?.data?.message || 'Cập nhật thông tin thất bại';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  if (loadingUser) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
        <LoadingSpin />
        <div style={{ marginTop: '16px' }}>Đang tải thông tin...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2}>Thông tin cá nhân</Title>
      
      {/* Card Thông tin cá nhân */}
      <Card 
        title="Thông tin tài khoản" 
        style={{ marginBottom: '24px' }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Avatar Preview */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Avatar
              size={120}
              src={(form.getFieldValue('photoURL') || user?.photoURL || '').trim() !== '' 
                ? (form.getFieldValue('photoURL') || user?.photoURL) 
                : undefined}
              icon={(!form.getFieldValue('photoURL') || form.getFieldValue('photoURL').trim() === '') && 
                     (!user?.photoURL || user.photoURL.trim() === '') 
                ? <UserOutlined /> 
                : null}
              style={{ marginBottom: '16px' }}
              alt={form.getFieldValue('displayName') || user?.displayName || 'User'}
            />
            <div>
              <Text strong style={{ fontSize: '18px', display: 'block' }}>
                {form.getFieldValue('displayName') || user?.displayName || 'Chưa có tên'}
              </Text>
              <Text type="secondary">{user?.email || ''}</Text>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              label="Email"
              name="email"
            >
              <Input
                prefix={<UserOutlined />}
                disabled
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </Form.Item>

            <Form.Item
              label="Tên hiển thị"
              name="displayName"
              rules={[
                { required: true, message: 'Tên hiển thị là bắt buộc' },
                { min: 5, message: 'Tên hiển thị phải có ít nhất 5 ký tự' },
                { max: 255, message: 'Tên hiển thị không được vượt quá 255 ký tự' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Nhập tên hiển thị (tối thiểu 5 ký tự)"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="URL Ảnh đại diện"
              name="photoURL"
              rules={[
                { type: 'url', message: 'URL không hợp lệ' },
                { max: 500, message: 'URL không được vượt quá 500 ký tự' }
              ]}
            >
              <Input
                prefix={<CameraOutlined />}
                placeholder="https://example.com/photo.jpg"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Số điện thoại"
              name="phoneNumber"
              rules={[
                { 
                  pattern: /^[0-9+\-\s()]+$/, 
                  message: 'Số điện thoại chỉ được chứa số, dấu +, -, khoảng trắng và dấu ngoặc' 
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve(); // Cho phép để trống
                    }
                    // Loại bỏ các ký tự không phải số để đếm
                    const digitsOnly = value.replace(/[^0-9]/g, '');
                    if (digitsOnly.length <= 9) {
                      return Promise.reject(new Error('Số điện thoại phải có nhiều hơn 9 số'));
                    }
                    return Promise.resolve();
                  }
                },
                { max: 50, message: 'Số điện thoại không được vượt quá 50 ký tự' }
              ]}
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="Nhập số điện thoại (tối thiểu 10 số, ví dụ: +84 123 456 789)"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
                block
              >
                Lưu thông tin
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
}

